import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable, type DataTableSelectionSingleChangeEvent, type DataTableStateEvent} from "primereact/datatable";
import {InputTextarea} from "primereact/inputtextarea";
import {Toast} from "primereact/toast";
import {Tooltip} from "primereact/tooltip";
import {type ReactNode, useEffect, useRef, useState} from "react";

import {LIFE_CYCLE_STATES, lifeCycleStateLabelKey, RevaLifeCycleState} from "@reva/shared/lifecycle-state";

import type {RevaFilterTab, SearchValuationItem} from "../../api/payload-types/search-valuation";
import {AssignValuationDialog} from "../../component/AssignValuationDialog";
import {CorrectionOwnerSelect} from "../../component/CorrectionOwnerSelect";
import {DeleteValuationDialog} from "../../component/DeleteValuationDialog";
import ErrorIndicator from "../../component/ErrorIndicator";
import {useRevaTranslation} from "../../hooks/languageHooks";
import {useQueryParam} from "../../hooks/navigationHooks";
import {useCheckPermission, useCheckPermissionAdmin} from "../../hooks/permissionHooks";
import {selectUsername} from "../../redux/authSlice";
import {selectPersistenceInitialized, selectPersistenceLoading, selectRevaId} from "../../redux/persistenceSlice";
import {deleteValuation, deleteValuationAndOrder, fetchPersistence} from "../../redux/persistenceThunk";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {
    selectCount,
    selectFreeText,
    selectInitialized,
    selectLoading,
    selectPossibleLifeCycleStates,
    selectQueryError,
    selectTableParams,
    selectValuations,
    type ValuationsTableParams,
} from "../../redux/searchValuationsSlice";
import {
    assignValuationOwnerThunk,
    loadSearchPageThunk,
    reopenForCorrectionThunk,
    updateValuationsTableParamsThunk,
} from "../../redux/searchValuationsThunk";
import {selectDarkTheme} from "../../redux/themeSlice";
import {getLifeCycleStateColors} from "../../util/chartUtils";
import {dataTableSortOrderFromString, dataTableSortOrderToString} from "../../util/componentUtil";
import {daysUntil, formatDate, formatDateAndTime} from "../../util/dateUtil";
import {errorDetailFromErrorI18n} from "../../util/errorUtil";
import {highlightFreeText} from "../../util/freeTextHighlight";
import {
    isDeletableLifeCycleState,
    PERMISSION_RULE_DELETION,
    PERMISSION_RULE_OWNERSHIP_ASSIGN,
} from "../../util/permissions";
import {ColorDotLabel, FilterCheckboxColumnHeader, TabControlCluster} from "./startPageWidgets";
import TabHint, {NoFilterHitsHint} from "./TabHint";

/**
 * Konfiguration der tab-spezifischen Zeilen-Aktion (Button in der Aktionsspalte, Pool/Zuweisen/Korrektur).
 * Jede Aktion läuft erst nach Bestätigung im {@link AssignValuationDialog} (mit Auftrags-Zusammenfassung).
 */
interface RowAction {
    /** Button-Icon: PrimeIcons-Klassenname oder ein Icon-Element. */
    icon: ReactNode;
    /** i18n-Key für Tooltip und aria-label. */
    labelKey: string;
    /** i18n-Key des Spaltenkopfs der Aktionsspalte. */
    headerKey: string;
    /**
     * Thunk-getriebene Standard-Aktion (WIP/POOL): Thunk bauen, Erfolgs-Growl, Log-Label. Fehlt sie
     * (CORRECTION), läuft die Aktion über den dedizierten runCorrection-Pfad im Bestätigungs-Dialog
     * (Pflicht-Kommentar + Owner-Auswahl).
     */
    thunkAction?: {
        /** i18n-Key der Erfolgs-Growl-Meldung. */
        successKey: string;
        /** Fehlertext fürs Log (console.error). */
        logLabel: string;
        /** Baut den zu dispatchenden Thunk; null = Aktion aktuell nicht ausführbar (z.B. kein eigener User). */
        makeThunk: (revaId: string) => ReturnType<typeof assignValuationOwnerThunk> | null;
    };
    /** i18n-Key der Dialog-Überschrift des Bestätigungs-Dialogs (Default siehe {@link AssignValuationDialog}). */
    confirmTitleKey?: string;
    /** i18n-Key des Bestätigungs-Buttons im Bestätigungs-Dialog (Default siehe {@link AssignValuationDialog}). */
    confirmLabelKey?: string;
}

interface ValuationsTableProps {
    className?: string;
    /**
     * Which of the two role-derived valuation tabs (#19113) this table instance shows. Sent as
     * the fixed, non-user-changeable `filterTab` request param; the backend applies the full
     * owner+lifecycle+permission logic for it. Also drives the fixed header title and hides the
     * (for both tabs redundant) owner column/filter.
     */
    tab: RevaFilterTab;
    /**
     * Optionaler Statistik-Umschalter, der links vom Reload-Button in die Kontroll-Ecke oben rechts
     * einsortiert wird (nur der Auftragspool nutzt ihn, siehe {@link TabControlCluster}).
     */
    statsToggle?: ReactNode;
    /** Optionales Statistik-Panel, das zwischen Hinweiszeile und Tabelle gerendert wird (Auftragspool). */
    statsPanel?: ReactNode;
    /**
     * Optionaler Zusatz-Reload: wird vom Reload-Button der Kontroll-Ecke zusätzlich zur Liste
     * ausgelöst (der Auftragspool lädt darüber sein aufgeklapptes Statistik-Panel neu — es gibt
     * bewusst nur EINEN Reload-Button je Tab).
     */
    onReloadStats?: () => void;
}

export default function ValuationsTable({
    className,
    tab,
    statsToggle,
    statsPanel,
    onReloadStats,
}: ValuationsTableProps) {
    const {t, language} = useRevaTranslation();
    const dispatch = useRevaDispatch();
    const toast = useRef<Toast>(null);

    const persistenceLoading = useRevaSelector(selectPersistenceLoading);
    const persistenceInitialized = useRevaSelector(selectPersistenceInitialized);
    const persistenceRevaId = useRevaSelector(selectRevaId);

    const [valuationToDelete, setValuationToDelete] = useState<SearchValuationItem | null>(null);
    // Tracks the row an assign/release action is in flight for (WIP's "release to pool" button or
    // POOL's "assign to me" button — only one of the two columns is ever rendered per tab instance).
    const [actionPendingRevaId, setActionPendingRevaId] = useState<string | null>(null);
    // Zeile, für die der Bestätigungs-Dialog der Zeilen-Aktion offen ist (null = kein Dialog).
    const [valuationToAssign, setValuationToAssign] = useState<SearchValuationItem | null>(null);
    // Nur CORRECTION-Tab: Pflicht-Kommentar (Grund) + gewählter neuer Owner (null = Auftragspool) des
    // Korrektur-Bestätigungsdialogs. Das Anzeige-Label (Personen-Name) wird nur für die Erfolgsmeldung
    // gebraucht und kommt zusammen mit dem Username aus CorrectionOwnerSelect.
    const [correctionComment, setCorrectionComment] = useState("");
    const [correctionOwner, setCorrectionOwner] = useState<{username: string; label: string} | null>(null);

    // Jeder Tab hat seinen eigenen Eintrag in der searchValuations-Slice (Record pro RevaFilterTab),
    // daher gehören alle hier selektierten Daten garantiert zu DIESEM Tab.
    const queryError = useRevaSelector((state) => selectQueryError(state, tab));
    const loading = useRevaSelector((state) => selectLoading(state, tab));
    const initialized = useRevaSelector((state) => selectInitialized(state, tab));
    const count = useRevaSelector((state) => selectCount(state, tab));
    const valuations = useRevaSelector((state) => selectValuations(state, tab));
    const possibleLifeCycleStates = useRevaSelector((state) => selectPossibleLifeCycleStates(state, tab));
    // Globaler Live-Freitextfilter (aus der Startseiten-Suchzeile): ändert er sich, lädt dieser
    // (gerade gemountete = aktive) Tab sich sofort neu (siehe Effekt unten).
    const freeText = useRevaSelector(selectFreeText);
    const ownUsername = useRevaSelector(selectUsername);
    const isDarkTheme = useRevaSelector(selectDarkTheme);

    const tableParams = useRevaSelector((state) => selectTableParams(state, tab));
    // Farben der fünf Lifecycle-Stati (Status-Filter-Checkboxen + Farbpunkt in der Statusspalte);
    // PreFinal/Final stimmen mit den Diagramm-Farben der Historie überein (siehe getLifeCycleStateColors).
    const lifeCycleColors = getLifeCycleStateColors(isDarkTheme);

    // Two-way binding between the status filter and a PER-TAB URL query param ("status" + tab, e.g.
    // "statusWIP"), so a filtered list stays shareable via its URL AND each tab is isolated: the
    // start-page tabs share one URL, so a single "status" param would carry a filter set on one tab
    // (e.g. Auftragspool) over to the others. A tab-specific key prevents that leak.
    const [, setStatusFilterParam] = useQueryParam(`status${tab}`);
    // Beim Lesen NICHT den useQueryParam-Wert (useLocation) verwenden: React Router führt
    // navigate() als Transition aus, d.h. direkt nach einem Tab-Wechsel kann useLocation noch
    // die Location VOR dem letzten setStatusFilterParam liefern (unter Last committet die
    // Transition erst nach dem Mount dieses Tabs). Der Init-Effect unten läuft aber genau
    // einmal — mit dem stalen Wert ginge der Status-Filter beim Tab-Wechsel verloren.
    // window.location ist dagegen immer aktuell.
    const statusFilterParam = new URLSearchParams(window.location.search).get(`status${tab}`);

    const isAdmin = useCheckPermissionAdmin();
    const canDelete = useCheckPermission(PERMISSION_RULE_DELETION);
    // Korrektur-Hinweis: mit reva-ownership-assign kann beim Öffnen zur Korrektur auch eine Person gewählt werden (CorrectionOwnerSelect).
    const canAssignToPerson = useCheckPermission(PERMISSION_RULE_OWNERSHIP_ASSIGN);
    // #19481: Der Research-Reiter zeigt bewusst nur Adresse, Erstellt am/von, Lizenz, Löschung und ID — kein Status,
    // keine Order-Nummer, keine Zeilen-Aktion (der Zweckwechsel sitzt im Header der Bewertungsansicht), keine Spalten
    // zur letzten Änderung. Sortiert wird trotzdem nach letzter Änderung (= letzter Nutzung), neueste zuerst.
    const isResearchTab = tab === "RESEARCH";

    // Läuft genau einmal pro Aktivierung (PrimeReacts TabView unmountet inaktive Tabs, Mount ==
    // Tab-Aktivierung). Erste Aktivierung: Tab-State initialisieren (Status-Deep-Link,
    // Korrekturen-Sortierung) und laden. Jede weitere Aktivierung: still neu laden — die per Tab
    // gespeicherten Params/Daten bleiben dabei sichtbar (stale-while-revalidate), sodass nichts
    // flackert, aber Änderungen aus anderen Tabs (z.B. "Mir zuweisen" im Auftragspool) ankommen.
    useEffect(() => {
        if (initialized) {
            void dispatch(loadSearchPageThunk(tab, true));
            return;
        }
        const initialParams: Partial<ValuationsTableParams> = {};
        // "status" carries a comma-separated list of lifecycle states (shareable deep link); keep only
        // the ones that are valid enum values, so a stale/garbled URL never poisons the filter.
        // NOT on the CORRECTION tab: it has no status column and the backend fixes it to Final, so a
        // leaked status from another tab's URL would be AND-intersected with Final and empty the list.
        if (tab !== "CORRECTION" && statusFilterParam) {
            const states = statusFilterParam
                .split(",")
                .filter((s): s is RevaLifeCycleState => LIFE_CYCLE_STATES.includes(s as RevaLifeCycleState));
            if (states.length > 0) initialParams.filterLifeCycleState = states;
        }
        // Der Korrekturen-Tab ist immer auf lifeCycleState=Final fixiert (siehe Backend), daher ist
        // standardmäßig das Datum der letzten Finalisierung (statt der letzten Änderung) sortiert.
        if (tab === "CORRECTION") {
            initialParams.sortField = "finalizeDate";
            initialParams.sortOrder = "DESC";
        }
        void dispatch(updateValuationsTableParamsThunk(tab, initialParams));
        // Bewusst nur beim Mount (= Tab-Aktivierung); `initialized` etc. absichtlich nicht als Trigger.
    }, []);

    // Ändert sich der globale Live-Freitextfilter, lädt dieser (aktive) Tab sofort neu. Beim ersten
    // Render übernimmt das der Mount-Effekt oben (der bereits mit dem aktuellen Freitext lädt), daher
    // hier den ersten Lauf überspringen, um ein doppeltes Laden zu vermeiden.
    const isFirstFreeTextRun = useRef(true);
    useEffect(() => {
        if (isFirstFreeTextRun.current) {
            isFirstFreeTextRun.current = false;
            return;
        }
        void dispatch(loadSearchPageThunk(tab, true));
    }, [dispatch, tab, freeText]);

    const onTableEvent = (event: DataTableStateEvent) => {
        // Aus der DataTable kommen nur Paging und Sortierung. Der Statusfilter (Header-Checkboxen, siehe
        // applyLifeCycleStateFilter) lebt außerhalb ihres Filter-States und wird unverändert durchgereicht,
        // damit Paging/Sortieren ihn nicht zurücksetzen; filterTab ist der fixe Tab dieser Instanz (#19113).
        void dispatch(
            updateValuationsTableParamsThunk(tab, {
                pageIndex: event.page,
                sortField: event.sortField,
                sortOrder: dataTableSortOrderToString(event.sortOrder),
                filterLifeCycleState: tableParams.filterLifeCycleState,
                filterTab: tab,
            }),
        );
    };

    /**
     * Apply a new set of lifecycle-state filter checkboxes: reflect it in the shareable "status" URL param
     * (comma-separated, cleared when empty) and reload from page 0. Spreads the current tableParams so the
     * other active filters/sorting are preserved (the reducer replaces the whole params object).
     * @param states the selected lifecycle states (empty = no status filter)
     */
    const applyLifeCycleStateFilter = (states: RevaLifeCycleState[]) => {
        const value = states.length > 0 ? states : null;
        setStatusFilterParam(value ? value.join(",") : null);
        void dispatch(
            updateValuationsTableParamsThunk(tab, {...tableParams, filterLifeCycleState: value, pageIndex: 0}),
        );
    };

    /** Toggle a single lifecycle state in the status filter. */
    const toggleLifeCycleState = (state: RevaLifeCycleState) => {
        const selected = tableParams.filterLifeCycleState ?? [];
        applyLifeCycleStateFilter(
            selected.includes(state) ? selected.filter((s) => s !== state) : [...selected, state],
        );
    };

    const onLoadValuation = (event: DataTableSelectionSingleChangeEvent<SearchValuationItem[]>) => {
        const revaId = event?.value?.revaId;
        if (revaId) {
            void dispatch(fetchPersistence({id: revaId}));
        }
    };

    const onReload = () => {
        void dispatch(loadSearchPageThunk(tab, true));
        onReloadStats?.();
    };

    // Die drei Zeilen-Aktionen (WIP: in den Pool geben, POOL: mir zuweisen, CORRECTION: zur Korrektur
    // wieder öffnen) laufen alle nach demselben Muster: Zeile sperren → Thunk dispatchen → Erfolgs-/
    // Fehler-Growl → Liste neu laden. Sie unterscheiden sich nur in Icon, Label, Erfolgsmeldung und dem
    // dispatchten Thunk; deshalb ein gemeinsamer Runner + eine pro Tab aufgelöste Konfiguration statt
    // dreier fast identischer Handler/Templates.
    // Zeigt die KONKRETE Backend-Fehlermeldung (error_de aus der QualifiedErrorMessage) an, wenn vorhanden —
    // z.B. "Die Bewertung wurde bereits abgeschlossen …" statt einer generischen Meldung. Fällt zurück auf
    // eine generische Meldung, wenn keine strukturierte Backend-Meldung vorliegt (z.B. Netzwerk-/500-Fehler).
    const actionErrorDetail = (err: unknown): string =>
        errorDetailFromErrorI18n(err, language) ?? t("general.error.unspecific");

    const runRowAction = async (revaId: string, action: RowAction) => {
        const thunk = action.thunkAction?.makeThunk(revaId);
        if (!action.thunkAction || !thunk) return;
        setActionPendingRevaId(revaId);
        try {
            await dispatch(thunk);
            toast.current?.show({
                severity: "info",
                summary: "Info",
                detail: `${revaId} ${t(action.thunkAction.successKey)}`,
            });
            onReload();
        } catch (err) {
            console.error(action.thunkAction.logLabel, err);
            toast.current?.show({severity: "error", summary: "Error", detail: actionErrorDetail(err)});
        } finally {
            setActionPendingRevaId(null);
        }
    };

    const closeCorrectionDialog = () => {
        setValuationToAssign(null);
        setCorrectionComment("");
        setCorrectionOwner(null);
    };

    // CORRECTION-Tab: Bewertung wieder öffnen (mit Pflicht-Kommentar) und — nur bei expliziter Personenwahl —
    // anschließend dieser Person zuweisen (sonst bleibt sie im Auftragspool, wohin reopenForCorrection sie legt).
    // Zweistufig im Client (kein atomarer Backend-Owner-Parameter); scheitert der Assign, bleibt sie sicher im Pool.
    const runCorrection = async (revaId: string, comment: string, owner: {username: string; label: string} | null) => {
        setActionPendingRevaId(revaId);
        try {
            await dispatch(reopenForCorrectionThunk(revaId, comment));
            if (owner) await dispatch(assignValuationOwnerThunk(revaId, owner.username));
            const assignmentDetail = owner
                ? t("start.valuations.reopenForCorrectionAssignedTo", {owner: owner.label})
                : t("start.valuations.reopenForCorrectionToPool");
            toast.current?.show({
                severity: "info",
                summary: "Info",
                detail: `${revaId} ${t("start.valuations.reopenForCorrectionSuccess")} ${assignmentDetail}`,
            });
            onReload();
        } catch (err) {
            console.error("Error reopening valuation for correction", err);
            toast.current?.show({severity: "error", summary: "Error", detail: actionErrorDetail(err)});
        } finally {
            setActionPendingRevaId(null);
        }
    };

    // Zeilen-Aktion des aktuellen Tabs (null = keine Aktionsspalte). WIP (in den Pool abgeben) und POOL
    // (mir zuweisen) sind Pool-/Selbst-Zuweisungen und daher für JEDEN erlaubt (kein reva-ownership-assign
    // nötig) — das Recht wird nur noch für das Zuweisen an eine ANDERE Person gebraucht (siehe
    // AssignUserDropdown / Backend LifeCycleGuards ASSIGN).
    const rowAction: RowAction | null =
        tab === "WIP"
            ? {
                  // Gegenstück zum "Mir zuweisen"-Pfeil des Auftragspools (ARROW_LEFT): raus in den Pool.
                  icon: PrimeIcons.ARROW_RIGHT,
                  labelKey: "start.valuations.releaseToPool",
                  headerKey: "start.valuations.releaseToPoolHeader",
                  thunkAction: {
                      successKey: "start.valuations.releaseToPoolSuccess",
                      logLabel: "Error releasing valuation to pool",
                      makeThunk: (revaId) => assignValuationOwnerThunk(revaId, null),
                  },
                  confirmTitleKey: "start.valuations.assignDialog.releaseToPoolTitle",
                  confirmLabelKey: "start.valuations.assignDialog.releaseToPoolConfirm",
              }
            : tab === "POOL"
              ? {
                    icon: PrimeIcons.ARROW_LEFT,
                    labelKey: "start.valuations.assignToMe",
                    headerKey: "start.valuations.assignToMe",
                    thunkAction: {
                        successKey: "start.valuations.assignToMeSuccess",
                        logLabel: "Error assigning valuation to self",
                        // Ohne bekannten eigenen Usernamen keine Selbstzuweisung möglich.
                        makeThunk: (revaId) => (ownUsername ? assignValuationOwnerThunk(revaId, ownUsername) : null),
                    },
                }
              : tab === "CORRECTION"
                ? {
                      // pi-wrench existiert als CSS-Klasse, aber nicht im PrimeIcons-Enum → Klassenstring direkt.
                      icon: "pi pi-wrench",
                      labelKey: "start.valuations.reopenForCorrection",
                      headerKey: "start.valuations.reopenForCorrectionHeader",
                      // Bewusst keine thunkAction: CORRECTION läuft über den dedizierten runCorrection-Pfad
                      // (Pflicht-Kommentar + Owner-Auswahl im Bestätigungs-Dialog).
                      confirmTitleKey: "start.valuations.assignDialog.correctionTitle",
                      confirmLabelKey: "start.valuations.reopenForCorrectionHeader",
                  }
                : null;

    /**
     * Template for column "Ext. Order-Number".
     */
    const externalOrderNumberTemplate = (data: SearchValuationItem) => {
        const allOrders = data.orders || [];
        const revaOrder = allOrders.find((order) => order.orderType === "Reva");
        const externalOrders = allOrders.filter((order) => order.orderType !== "Reva");
        if (externalOrders.length > 0) {
            return (
                <>
                    {externalOrders.map((order) => {
                        return (
                            <div key={order.valueOrderGuid}>
                                <span id={`value-order-${order.valueOrderGuid}`} className="externalOrderNumber">
                                    {highlightFreeText(order.valueExternalOrderNumber, freeText)}
                                </span>
                                <Tooltip target={`#value-order-${order.valueOrderGuid}`}>
                                    {t("order.order.orderType")} {t(`order.orderType.${order.orderType}`)}
                                </Tooltip>
                            </div>
                        );
                    })}
                </>
            );
        } else if (revaOrder != null) {
            return (
                <>
                    <span id={`value-order-${revaOrder.valueOrderGuid}`} className="externalOrderNumber revaOrder">
                        {highlightFreeText(revaOrder.valueExternalOrderNumber, freeText)}
                    </span>
                    <Tooltip target={`#value-order-${revaOrder.valueOrderGuid}`}>
                        {t("order.order.orderType")} {t(`order.orderType.${revaOrder.orderType}`)}
                    </Tooltip>
                </>
            );
        }
        return null;
    };

    /**
     * Template for column "Address".
     */
    const addressTemplate = (data: SearchValuationItem) => {
        if (data.referenceAddress) {
            return (
                <>
                    <div id={`address-${data.revaId}`}>
                        <span>{highlightFreeText(data.address, freeText)}</span>
                        <div className="text-surface-400 dark:text-surface-400 mt-1">
                            {t("start.valuations.ref")}: {highlightFreeText(data.referenceAddress, freeText)}
                        </div>
                    </div>
                    <Tooltip target={`#address-${data.revaId}`}>
                        <div>
                            {t("start.valuations.orderAddress")}: {data.address}
                        </div>
                        <div>
                            {t("start.valuations.referenceAddress")}: {data.referenceAddress}
                        </div>
                    </Tooltip>
                </>
            );
        }
        return <span>{highlightFreeText(data.address, freeText)}</span>;
    };

    /**
     * #19481: Spalte „Löschung in … Tagen" des Research-Reiters — Restlaufzeit bis zur automatischen Löschung ohne
     * weitere Nutzung, das genaue Datum im Tooltip. Jedes Öffnen setzt die Frist neu.
     */
    const retentionTemplate = (data: SearchValuationItem) => {
        const days = daysUntil(data.retentionExpiresAt);
        if (days === null) return null;
        return (
            <span
                className="retentionExpiresAt"
                title={t("purpose.deletionAt", {date: formatDate(data.retentionExpiresAt, language)})}
            >
                {days === 0 ? t("purpose.deletionToday") : t("purpose.deletionInDays", {count: days})}
            </span>
        );
    };

    /**
     * Template for column "Lifecycle state": a colored dot (same color as the matching status-filter
     * checkbox) plus the localized state label (#18860).
     */
    const lifeCycleStateTemplate = (data: SearchValuationItem) =>
        data.lifeCycleState ? (
            <ColorDotLabel
                color={lifeCycleColors[data.lifeCycleState]}
                label={t(lifeCycleStateLabelKey(data.lifeCycleState))}
            />
        ) : null;

    /**
     * Gemeinsames Template der tab-spezifischen Zeilen-Aktionsspalte (WIP: in den Pool geben, POOL:
     * mir zuweisen, CORRECTION: zur Korrektur wieder öffnen) — der Button ist bis auf Icon/Label/Aktion
     * identisch, daher aus der {@link RowAction}-Konfiguration gespeist.
     */
    const rowActionTemplate = (action: RowAction) => (data: SearchValuationItem) => (
        <Button
            text
            rounded
            icon={action.icon}
            // Unsichtbar vergrößerte Klickfläche (+3px je Richtung) über ein ::after-Overlay,
            // damit der kleine Icon-Button leichter zu treffen ist, ohne das Layout zu ändern.
            className="relative after:absolute after:-inset-[3px] after:content-['']"
            aria-label={t(action.labelKey)}
            loading={actionPendingRevaId === data.revaId}
            disabled={actionPendingRevaId !== null}
            tooltip={t(action.labelKey)}
            tooltipOptions={{position: "top"}}
            onClick={(e) => {
                e.stopPropagation();
                setValuationToAssign(data);
            }}
        />
    );

    /**
     * Template for action column.
     */
    const actionIconsTemplate = (data: SearchValuationItem) => {
        // Ab dem vorläufigen Abschluss lehnt reva-rest das Löschen immer ab (#19530).
        if (!isDeletableLifeCycleState(data.lifeCycleState)) return null;
        return (
            <span
                className="pi pi-trash"
                onClick={(e) => {
                    e.stopPropagation();
                    // This will show the Deletion Dialog
                    setValuationToDelete(data);
                }}
            />
        );
    };

    const deleteOrder = async (withOrder: boolean, reason?: string, explanation?: string) => {
        if (!valuationToDelete) {
            return;
        }
        const revaIdToDelete = valuationToDelete.revaId;
        // With-order deletion always supplies reason+explanation (the dialog requires them in that path).
        const result = withOrder
            ? await dispatch(deleteValuationAndOrder(revaIdToDelete, reason!, explanation!))
            : await dispatch(deleteValuation(revaIdToDelete));
        setValuationToDelete(null);
        if (result?.revaId != null) {
            toast.current?.show({severity: "info", summary: "Info", detail: `${result.revaId} gelöscht!`});
            onReload();
        }
    };

    // Spalten-Header "Status": der Spaltentitel plus je eine farbige Checkbox pro Lifecycle-Status
    // zum Ein-/Ausblenden (gleicher Mechanismus wie der "Typ"-Header der Historie, siehe
    // FilterCheckboxColumnHeader). Serverseitige Filterung (filterLifeCycleState-Liste).
    const selectedLifeCycleStates = tableParams.filterLifeCycleState ?? [];
    // Angeboten werden nur Stati, die in diesem Tab überhaupt vorkommen KÖNNEN (Backend-berechnet aus
    // Tab-Semantik + Rechten, z.B. nie "Final" unter "Meine Bewertungen") — plus aktuell ausgewählte
    // Stati (etwa aus einem Deep-Link), damit ein aktiver Filter immer abwählbar bleibt. Vor der
    // ersten Antwort (possibleLifeCycleStates == null) erscheinen nur die ausgewählten.
    const shownLifeCycleStates = LIFE_CYCLE_STATES.filter(
        (state) => (possibleLifeCycleStates?.includes(state) ?? false) || selectedLifeCycleStates.includes(state),
    );
    const lifeCycleStateColumnHeader = (
        <FilterCheckboxColumnHeader
            title={t("start.valuations.lifeCycleState")}
            items={shownLifeCycleStates.map((state) => ({
                inputId: `lifeCycleStateFilter${state}`,
                color: lifeCycleColors[state],
                checked: selectedLifeCycleStates.includes(state),
                tooltip: t(lifeCycleStateLabelKey(state)),
                onToggle: () => toggleLifeCycleState(state),
            }))}
        />
    );

    // Standalone hint row ABOVE the table on every tab, so the reload button sits at the exact same
    // top-right spot on all start-page tabs (and, for the pool, the stats toggle just left of it). The
    // TEAMADMIN tab has no explanatory text (its sub-tab name + the AdminTab explanation already label the
    // list) — then TabHint shows only the right-aligned control cluster. Layout/typography is
    // centralized in TabHint (shared by all start-page tabs).
    const hintText =
        tab === "POOL" ? (
            t("start.valuations.poolHint")
        ) : tab === "CORRECTION" ? (
            <>
                {t("start.valuations.correctionHintBefore")} <i className="pi pi-wrench" />{" "}
                {t("start.valuations.correctionHintAfter", {context: canAssignToPerson ? "assign" : undefined})}
            </>
        ) : tab === "WIP" ? (
            t("start.valuations.titleWip")
        ) : tab === "REVISION" ? (
            t("start.valuations.revisionHint")
        ) : tab === "RESEARCH" ? (
            t("start.valuations.researchHint")
        ) : null;

    const aboveTableHint = (
        <TabHint action={<TabControlCluster statsToggle={statsToggle} onReload={onReload} />}>{hintText}</TabHint>
    );

    // Bei aktivem Freitext ohne Treffer: statt Tab-Hinweis + leerer Tabelle nur eine kurze
    // "keine Treffer"-Zeile rendern (weniger Rauschen; die Badges zeigen, wo es Treffer gibt).
    if (freeText != null && !loading && count === 0 && !queryError) {
        return (
            <div id="valuationsTable" className={className}>
                <NoFilterHitsHint freeText={freeText} />
            </div>
        );
    }

    return (
        <div id="valuationsTable" className={className}>
            {aboveTableHint}
            {statsPanel}
            <ErrorIndicator
                titleI18nKey="start.valuations.queryError"
                errorObject={queryError}
                hasContactLink={true}
                classNameContactLink="mb-6"
            />
            <Toast ref={toast} position="top-center" />
            <DeleteValuationDialog
                revaId={valuationToDelete?.revaId ?? null}
                hasValueOrders={!!valuationToDelete?.orders.length}
                onCancel={() => setValuationToDelete(null)}
                onDelete={(deleteOrders, reason, comment) => void deleteOrder(deleteOrders, reason, comment)}
            />
            {rowAction && (
                <AssignValuationDialog
                    valuation={valuationToAssign}
                    titleKey={rowAction.confirmTitleKey}
                    confirmLabelKey={rowAction.confirmLabelKey}
                    // Im Korrekturen-Tab ist der Grund Pflicht: Bestätigen erst mit nicht-leerem Kommentar.
                    confirmDisabled={tab === "CORRECTION" && correctionComment.trim().length === 0}
                    onCancel={closeCorrectionDialog}
                    onConfirm={() => {
                        const target = valuationToAssign;
                        if (target) {
                            if (tab === "CORRECTION")
                                void runCorrection(target.revaId, correctionComment, correctionOwner);
                            else void runRowAction(target.revaId, rowAction);
                        }
                        closeCorrectionDialog();
                    }}
                >
                    {tab === "CORRECTION" && valuationToAssign && (
                        <div className="mt-4">
                            <label htmlFor="correction-comment" className="block mb-2">
                                {t("start.valuations.reopenForCorrectionReason")}
                            </label>
                            <InputTextarea
                                id="correction-comment"
                                className="w-full"
                                rows={4}
                                autoFocus
                                value={correctionComment}
                                onChange={(e) => setCorrectionComment(e.target.value ?? "")}
                            />
                            <CorrectionOwnerSelect
                                revaId={valuationToAssign.revaId}
                                value={correctionOwner?.username ?? null}
                                onChange={(username, label) => setCorrectionOwner(username ? {username, label} : null)}
                            />
                        </div>
                    )}
                </AssignValuationDialog>
            )}
            {/* key=freeText: PrimeReact cached die Column-Bodies — ohne Remount könnten die
                Treffer-Highlights einem Filterwechsel hinterherhinken (siehe DoneTab). */}
            <DataTable<SearchValuationItem[]>
                key={freeText ?? ""}
                size="small"
                lazy
                paginator
                rows={tableParams.pageSize}
                totalRecords={count}
                // Beim Neuladen (auch dem stillen Reload bei Tab-Aktivierung) bleiben die zuletzt
                // geladenen Zeilen dieses Tabs unter dem Lade-Overlay sichtbar — kein Flackern.
                // !initialized deckt den ersten Frame vor dem Init-Effect ab (kein "keine Einträge"-Blitzer).
                loading={loading || persistenceLoading || !initialized}
                disabled={!persistenceInitialized || !!persistenceRevaId}
                value={valuations}
                onPage={onTableEvent}
                onSort={onTableEvent}
                first={tableParams.pageIndex * tableParams.pageSize}
                dataKey="revaId"
                selectionMode="single"
                onSelectionChange={onLoadValuation}
                stripedRows
                sortOrder={dataTableSortOrderFromString(tableParams.sortOrder)}
                sortField={tableParams.sortField}
            >
                {/* Im Korrekturen-Tab ist lifeCycleState ohnehin fix auf Final, eine Status-Spalte/-Filter wäre redundant. */}
                {tab !== "CORRECTION" && !isResearchTab && (
                    <Column header={lifeCycleStateColumnHeader} field="lifeCycleState" body={lifeCycleStateTemplate} />
                )}
                {!isResearchTab && (
                    <Column
                        header={t("start.valuations.externalOrderNumber")}
                        field="valueExternalOrderNumber"
                        body={externalOrderNumberTemplate}
                    />
                )}
                {rowAction && <Column header={t(rowAction.headerKey)} body={rowActionTemplate(rowAction)} />}
                <Column header={t("start.valuations.address")} field="address" body={addressTemplate} />
                <Column
                    header={t("start.valuations.createdDate")}
                    field="createdDate"
                    sortable
                    body={(data: SearchValuationItem) => formatDateAndTime(data.createdDate, language)}
                />
                <Column
                    header={t("start.valuations.createdUser")}
                    field="createdUser"
                    body={(data: SearchValuationItem) =>
                        highlightFreeText(data.createdUserName ?? data.createdUser, freeText)
                    }
                />
                {!isResearchTab && (
                    <Column
                        header={t("start.valuations.lastChangedDate")}
                        field="lastChangedDate"
                        sortable
                        body={(data: SearchValuationItem) => formatDateAndTime(data.lastChangedDate, language)}
                    />
                )}
                {!isResearchTab && (
                    <Column
                        header={t("start.valuations.lastChangedUser")}
                        field="lastChangedUser"
                        body={(data: SearchValuationItem) =>
                            highlightFreeText(data.lastChangedUserName ?? data.lastChangedUser, freeText)
                        }
                    />
                )}
                {tab === "CORRECTION" && (
                    <Column
                        header={t("start.valuations.finalizeDate")}
                        field="finalizeDate"
                        sortable
                        body={(data: SearchValuationItem) => formatDateAndTime(data.finalizeDate, language)}
                    />
                )}
                {tab === "CORRECTION" && (
                    <Column
                        header={t("start.valuations.finalizeUser")}
                        field="finalizeUser"
                        body={(data: SearchValuationItem) =>
                            highlightFreeText(data.finalizeUserName ?? data.finalizeUser, freeText)
                        }
                    />
                )}
                {/* #19113: WIP is always "me", POOL always unassigned, CORRECTION scoped by lifecycle —
                    an owner column would be redundant there. The Alle/Team tab (TEAMADMIN) and the read-only
                    revision "Alle Bewertungen" tab (REVISION, #19125) show all users' valuations, so the owner
                    is shown (who currently holds each one). */}
                {(tab === "TEAMADMIN" || tab === "REVISION") && (
                    <Column
                        header={t("start.valuations.owner")}
                        field="owner"
                        body={(data: SearchValuationItem) =>
                            highlightFreeText(data.ownerFullName ?? data.owner, freeText)
                        }
                    />
                )}
                {(isAdmin || isResearchTab) && (
                    <Column
                        header={t("start.valuations.license")}
                        field="license"
                        body={(data: SearchValuationItem) =>
                            highlightFreeText(data.licenseDisplayName ?? data.license, freeText)
                        }
                    />
                )}
                {isResearchTab && (
                    <Column header={t("purpose.deletionColumn")} field="retentionExpiresAt" body={retentionTemplate} />
                )}
                <Column
                    header={t("start.valuations.id")}
                    field="revaId"
                    body={(data: SearchValuationItem) => highlightFreeText(data.revaId, freeText)}
                />
                {canDelete && !isResearchTab && <Column body={actionIconsTemplate} />}
            </DataTable>
        </div>
    );
}
